import { CHOOSE_ACCOUNT_TYPE_FAQ } from 'common/constants';
import {
  ATBeaconLoadingSpinner,
  ATBeaconNotification,
  ATSelectionTile,
} from 'components/common/beacon/beaconRegistration';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useGetInternalAccountsQuery } from 'src/api/internal/internalApi';
import { PageRoutes } from 'src/common/constants';
import Accordion from 'src/components/common/Accordion/Accordion';
import { setAccountType } from 'src/features/transfer/transferSlice';
import { RootState } from 'src/state/store';
import { AccountType, AccountTypeDisplayNames } from 'utils/constants';
import './ChooseAccountType.scss';
import MutualFundOnlyModal from './MutualFundOnlyModal';
import ValidationModal from './ValidationModal';

interface ChooseAccountTypeProps {
  commonAccountTypes: AccountType[];
  additionalAccountTypes: AccountType[];
  setIsStepValid: (isEnabled: boolean) => void;
  setShowValidation: (isEnabled: boolean) => void;
  showValidation: boolean;
  setShowValidationModal: (isEnabled: boolean) => void;
  showValidationModal: boolean;
  setIsAccountAvailable: (isEnabled: boolean) => void;
}

const ValidationMessage: React.FC<{ onDismiss: () => void }> = ({ onDismiss }) => (
  <ATBeaconNotification
    type="inline"
    variant="warning"
    dismissible
    onBeaconEventDismiss={onDismiss}
  >
    Please choose an account to move forward
  </ATBeaconNotification>
);

const AccountTypeOption: React.FC<{
  type: AccountType;
  isSelected: boolean;
  onSelect: () => void;
  testId: string;
}> = ({ type, isSelected, onSelect, testId }) => (
  <ATSelectionTile>
    <label data-testid={testId} slot="label" htmlFor={`account-type-${type}`}>
      {AccountTypeDisplayNames[type]}
    </label>
    <input
      id={`account-type-${type}`}
      type="radio"
      name="selection"
      checked={isSelected}
      onChange={onSelect}
    />
  </ATSelectionTile>
);

const FAQSection: React.FC = () => (
  <div className="faq-container">
    <Accordion items={CHOOSE_ACCOUNT_TYPE_FAQ} />
  </div>
);

const ChooseAccountType: React.FC<ChooseAccountTypeProps> = ({
  commonAccountTypes,
  additionalAccountTypes,
  setIsStepValid,
  showValidation,
  setShowValidation,
  showValidationModal,
  setShowValidationModal,
  setIsAccountAvailable,
}) => {
  const dispatch = useDispatch();
  const selectedOption = useSelector((state: RootState) => state.transfer.accountType);
  const nasuLink = useSelector((state: RootState) => state.appConfig.nasuUrl);
  const [isAdditionalSelected, setIsAdditionalSelected] = useState<boolean>(
    selectedOption ? additionalAccountTypes.includes(selectedOption) : false,
  );
  const { data: internalaccounts, isError, isLoading } = useGetInternalAccountsQuery();
  const navigate = useNavigate();
  useEffect(() => {
    if (isError) {
      navigate(PageRoutes.ERROR);
    }
  }, [isError, navigate]);
  const handleSelectionChange = (option: AccountType, isAdditional: boolean) => {
    dispatch(setAccountType(option));
    setIsStepValid(!!option);
    setIsAdditionalSelected(isAdditional);
    validateSelectedAccountType(option);
  };

  const handleAdditionalSelected = () => {
    setIsAdditionalSelected(!isAdditionalSelected);
    dispatch(setAccountType(null));
    setIsStepValid(false);
  };
  const validateSelectedAccountType = (option: AccountType) => {
    const isValid = internalaccounts?.some(
      (account) => account.agreementTypeValue?.enumerationID === option,
    );
    setIsAccountAvailable(!!isValid);
  };
  const handleClose = () => {
    setShowValidationModal(false);
  };
  const renderAccountOptions = (accountTypes: AccountType[], isAdditional: boolean) =>
    accountTypes.map((type, index) => (
      <div
        key={index}
        className="b-grid-item--full-col b-xs:12-col b-sm:12-col b-md:12-col b-lg:6-col"
      >
        <AccountTypeOption
          type={type}
          isSelected={selectedOption === type}
          onSelect={() => handleSelectionChange(type, isAdditional)}
          testId={AccountTypeDisplayNames[type]}
        />
      </div>
    ));

  if (isLoading) {
    return (
      <div className="content-container b-grid loading-container">
        <ATBeaconLoadingSpinner scale={'jumbo'} />
      </div>
    );
  }

  return (
    <div className="content-container b-grid">
      <div className="content-left b-grid-item--full-col b-xs:12-col b-sm:12-col b-md:8-col b-lg:8-col">
        <div className="b-grid">
          <span className="b-grid-item--full-col b-xs:12-col b-sm:12-col b-md:12-col b-lg:12-col">
            <label className="b-typography-sans-serif--headline--100 b-font-weight-bold">
              Common account types
            </label>
          </span>
          {showValidation && (
            <div className="b-grid-item--full-col b-xs:12-col b-sm:12-col b-md:12-col b-lg:12-col">
              <ValidationMessage onDismiss={() => setShowValidation(false)} />
            </div>
          )}
          {renderAccountOptions(commonAccountTypes, false)}
          <div className="b-grid-item--full-col b-xs:12-col b-sm:12-col b-md:12-col b-lg:6-col">
            <ATSelectionTile data-testid={'additional-account-type'}>
              <label slot="label" htmlFor="selection-additional">
                Additional account types
              </label>
              <input
                id="selection-additional"
                type="radio"
                name="selection-additional"
                checked={isAdditionalSelected}
                onChange={handleAdditionalSelected}
              />
            </ATSelectionTile>
          </div>
        </div>

        {isAdditionalSelected && (
          <div className="b-grid additional-grid">
            {renderAccountOptions(additionalAccountTypes, true)}
          </div>
        )}
      </div>
      <div className="b-grid-item--full-col b-xs:12-col b-sm:12-col b-md:4-col b-lg:4-col">
        <FAQSection />
      </div>
      {showValidationModal && (
        <ValidationModal
          open={showValidationModal}
          onClose={handleClose}
          nasuLink={nasuLink}
          accountTypeId={selectedOption}
        ></ValidationModal>
      )}
      {internalaccounts && internalaccounts.length === 0 && (
        <MutualFundOnlyModal open={true} />
      )}
    </div>
  );
};

export default ChooseAccountType;
